import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { LiveChatWidget } from '@livechat/widget-react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { resolveApiAssetUrl } from '../lib/api';
import {
  buildLiveChatConfig, isLiveChatSuppressed, openLiveChat,
  subscribeLiveChatSuppression,
} from './liveChatConfig';
import LiveChatDefaultIcon from './LiveChatDefaultIcon';
import Comm100Support from './Comm100Support';

const LiveChatSupport = () => {
  const settings = useSettings();
  const { user } = useAuth();
  const [widgetVisibility, setWidgetVisibility] = useState('minimized');
  const [suppressed, setSuppressed] = useState(isLiveChatSuppressed);
  const [iconFailed, setIconFailed] = useState(false);
  // Widget'a ilk kez dokunulana kadar sessiz kalınır: bastırma hiç
  // istenmemişse açılışta 'minimize' çağırmak, oyuncunun elle büyüttüğü
  // pencereyi kapatırdı.
  const touchedRef = useRef(false);
  const iconUrl = resolveApiAssetUrl(settings.livechat_icon_url);
  const config = useMemo(
    () => buildLiveChatConfig(settings, user),
    [settings, user],
  );

  const handleVisibilityChanged = useCallback(({ visibility }) => {
    setWidgetVisibility(visibility);
  }, []);

  const handleReady = useCallback(({ state }) => {
    setWidgetVisibility(state.visibility);
    // Çerçeve widget yüklenmeden önce açılmış olabilir: bastırma isteği o anda
    // sessizce düşerdi (`window.LiveChatWidget` henüz yok).
    if (isLiveChatSuppressed()) {
      touchedRef.current = true;
      window.LiveChatWidget?.call?.('hide');
    }
  }, []);

  useEffect(() => subscribeLiveChatSuppression(setSuppressed), []);

  useEffect(() => {
    if (!suppressed && !touchedRef.current) return;
    touchedRef.current = true;
    window.LiveChatWidget?.call?.(suppressed ? 'hide' : 'minimize');
  }, [suppressed]);

  // Yüklenen ikon değişince kırık görsel işareti sıfırlanır.
  useEffect(() => setIconFailed(false), [iconUrl]);

  useEffect(() => {
    document.body.classList.toggle(
      'livechat-maximized',
      widgetVisibility === 'maximized',
    );
    return () => document.body.classList.remove('livechat-maximized');
  }, [widgetVisibility]);

  // LiveChat kurulu değilse Comm100 (kuruluysa) yüklenir; ikisi birden yüklenmez.
  if (!config) return <Comm100Support />;

  return (
    <>
      <LiveChatWidget
        license={config.license}
        group={config.group}
        customerName={config.customerName}
        customerEmail={config.customerEmail}
        sessionVariables={config.sessionVariables}
        onReady={handleReady}
        onVisibilityChanged={handleVisibilityChanged}
      />
      {!suppressed && widgetVisibility !== 'maximized' && widgetVisibility !== 'hidden' && (
        <button
          type="button"
          className="livechat-custom-launcher"
          onClick={openLiveChat}
          aria-label="Canlı desteği aç"
          title="Canlı Destek 7/24"
        >
          {iconUrl && !iconFailed ? (
            <img
              src={iconUrl}
              alt=""
              draggable="false"
              onError={() => setIconFailed(true)}
            />
          ) : (
            <LiveChatDefaultIcon />
          )}
        </button>
      )}
    </>
  );
};

export default LiveChatSupport;
